export function formatDateTime(dateString: string | Date | null | undefined): string {
  if (!dateString || dateString === 'null' || dateString === 'undefined' || dateString === '') {
    return 'Nunca';
  }

  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;

    if (isNaN(date.getTime())) {
      return 'Nunca';
    }

    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  } catch {
    return 'Nunca';
  }
}

/**
 * Data e hora separadas, para a tela alinhar cada uma na sua ponta:
 * `{ data: "02 de Ago de 2026", hora: "20:07" }`.
 *
 * Vêm separadas de propósito. Numa string só, o começo da hora depende da
 * largura do nome do mês, e a coluna fica com as horas dançando — com o mês
 * abreviado ainda dança, porque a fonte é proporcional e "Mai" não tem a mesma
 * largura de "Jan". Com dois pedaços, a tela prende a data à esquerda e a hora
 * à direita, e as duas pontas ficam retas.
 *
 * Quando não há data, `hora` vem vazia e `data` traz "Nunca".
 */
export function formatDataHoraPartes(dateString: string | Date | null | undefined): {
  data: string;
  hora: string;
} {
  if (!dateString || dateString === 'null' || dateString === 'undefined' || dateString === '') {
    return { data: 'Nunca', hora: '' };
  }

  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    if (isNaN(date.getTime())) return { data: 'Nunca', hora: '' };

    const dia = String(date.getDate()).padStart(2, '0');
    // O Intl abrevia com ponto em pt-BR ("ago."); tiramos o ponto e subimos a
    // inicial — é escolha de exibição desta tela.
    const abrev = new Intl.DateTimeFormat('pt-BR', { month: 'short' })
      .format(date)
      .replace('.', '');
    const mes = abrev.charAt(0).toUpperCase() + abrev.slice(1);
    const hora = new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);

    return { data: `${dia} de ${mes} de ${date.getFullYear()}`, hora };
  } catch {
    return { data: 'Nunca', hora: '' };
  }
}

/** Data sem hora, como a API devolve toda coluna `date`: "2026-09-11". */
const DATA_SEM_HORA = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Converte o texto da API em Date para exibição.
 *
 * "2026-09-11" precisa virar meia-noite LOCAL, não UTC. `new Date("2026-09-11")`
 * é lido como UTC pela spec, e formatar isso no fuso do Brasil mostra 10/09 —
 * um dia a menos em toda coluna `date` do sistema. Por isso a data sem hora é
 * montada por partes, que é o construtor que usa o fuso local.
 *
 * Texto com hora (timestamp) continua no caminho normal: ali o fuso é
 * informação de verdade, e converter para local é o comportamento certo.
 */
function paraDataLocal(texto: string): Date {
  const partes = DATA_SEM_HORA.exec(texto);
  if (!partes) return new Date(texto);

  const [, ano, mes, dia] = partes;
  return new Date(Number(ano), Number(mes) - 1, Number(dia));
}

export function formatDate(dateString: string | Date | null | undefined): string {
  if (!dateString || dateString === 'null' || dateString === 'undefined' || dateString === '') {
    return 'Não informado';
  }

  try {
    const date = typeof dateString === 'string' ? paraDataLocal(dateString) : dateString;

    if (isNaN(date.getTime())) {
      return 'Não informado';
    }

    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  } catch {
    return 'Não informado';
  }
}

export function formatRelativeTime(dateString: string | Date | null | undefined): string {
  if (!dateString || dateString === 'null' || dateString === 'undefined' || dateString === '') {
    return 'Nunca';
  }

  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;

    if (isNaN(date.getTime())) {
      return 'Nunca';
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Agora mesmo';
    if (diffMins < 60) return `Há ${diffMins} minuto${diffMins > 1 ? 's' : ''}`;
    if (diffHours < 24) return `Há ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    if (diffDays < 7) return `Há ${diffDays} dia${diffDays > 1 ? 's' : ''}`;

    return formatDateTime(date);
  } catch {
    return 'Nunca';
  }
}

/** Usuário cadastrado há no máximo 7 dias — usado pro badge "Novo". */
export function isNewUser(createdAt: string | Date | null | undefined): boolean {
  if (!createdAt) return false;

  const date = typeof createdAt === 'string' ? new Date(createdAt) : createdAt;
  if (isNaN(date.getTime())) return false;

  const diffDays = (Date.now() - date.getTime()) / 86400000;
  return diffDays >= 0 && diffDays < 7;
}

export function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return 'Não informado';

  const cleaned = phone.replace(/\D/g, '');

  if (cleaned.length === 11) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }

  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }

  if (cleaned.length === 13 && cleaned.startsWith('55')) {
    return `+55 (${cleaned.slice(2, 4)}) ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`;
  }

  return phone;
}

export function formatNumber(value: number | null | undefined, decimals = 0): string {
  if (value === null || value === undefined) return '0';

  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return 'R$ 0,00';

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

/**
 * Rótulo de data do gráfico de atividade (um ponto por dia).
 * "YYYY-MM-DD" é lido como data local — `new Date('2026-09-17')` seria meia-noite
 * UTC e, no fuso de Brasília, cairia no dia anterior.
 */
export function formatChartDate(dateString: string): string {
  if (!dateString) return '';

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(dateString);

  if (isNaN(date.getTime())) return dateString;

  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short' }).format(date);
}
